import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import Api from '../../services/api';
import { VehicleModel } from '../../types/api.types';

export enum VehicleErrorType {
  NONE = 'NONE',
  NOT_FOUND = 'NOT_FOUND',
  NETWORK_ERROR = 'NETWORK_ERROR',
  UNKNOWN = 'UNKNOWN'
}

interface VehicleState {
  vehicle: VehicleModel | null;
  error: VehicleErrorType;
  fetching: boolean;
  hasVehicle: boolean;
  vehicleIsDrivable: boolean;
  additionalInformation: string;
  currentMileage: number;
}

const initialState: VehicleState = {
  vehicle: null,
  error: VehicleErrorType.NONE,
  fetching: false,
  hasVehicle: false,
  vehicleIsDrivable: true,
  additionalInformation: '',
  currentMileage: 0
};

// Async thunks
export const lookupVehicle = createAsyncThunk(
  'vehicle/lookup',
  async (vinOrCode: string) => {
    const vehicle = await Api.getVehicle(vinOrCode);
    return vehicle;
  }
);

const vehicleSlice = createSlice({
  name: 'vehicle',
  initialState,
  reducers: {
    setVehicle: (state, action: PayloadAction<VehicleModel>) => {
      state.vehicle = action.payload;
      state.hasVehicle = true;
      state.error = VehicleErrorType.NONE;
      state.currentMileage = action.payload.mileage || 0;
    },
    clearVehicle: (state) => {
      state.vehicle = null;
      state.hasVehicle = false;
      state.error = VehicleErrorType.NONE;
      state.vehicleIsDrivable = true;
      state.additionalInformation = '';
      state.currentMileage = 0;
    },
    setVehicleError: (state, action: PayloadAction<VehicleErrorType>) => {
      state.error = action.payload;
      state.fetching = false;
    },
    setVehicleDrivable: (state, action: PayloadAction<boolean>) => {
      state.vehicleIsDrivable = action.payload;
    },
    setAdditionalInformation: (state, action: PayloadAction<string>) => {
      state.additionalInformation = action.payload;
    },
    setCurrentMileage: (state, action: PayloadAction<number>) => {
      state.currentMileage = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(lookupVehicle.pending, (state) => {
      state.fetching = true;
      state.error = VehicleErrorType.NONE;
    });
    builder.addCase(lookupVehicle.fulfilled, (state, action) => {
      state.vehicle = action.payload;
      state.hasVehicle = true;
      state.fetching = false;
      state.error = VehicleErrorType.NONE;
      state.currentMileage = action.payload.mileage || 0;
    });
    builder.addCase(lookupVehicle.rejected, (state, action) => {
      state.fetching = false;
      state.hasVehicle = false;
      
      if (action.error.message?.includes('404') || action.error.message?.includes('Not Found')) {
        state.error = VehicleErrorType.NOT_FOUND;
      } else if (action.error.message?.includes('Network')) {
        state.error = VehicleErrorType.NETWORK_ERROR;
      } else {
        state.error = VehicleErrorType.UNKNOWN;
      }
    });
  }
});

export const {
  setVehicle,
  clearVehicle,
  setVehicleError,
  setVehicleDrivable,
  setAdditionalInformation,
  setCurrentMileage
} = vehicleSlice.actions;

export default vehicleSlice.reducer;